import { Map } from 'immutable';
import pluralize from 'pluralize';

import { getActualDate, getDateDifference, getFormattedDate } from './time';
import { formatFileSize } from './number';
import { getFileError } from './validations/files';
import { isTruthy } from './bool';
import api from './api/request';

import { ENDPOINT, HEADERS } from '../constants/api';
import { EMPTY, SEPARATOR } from '../constants';

const { CONTENT_TYPE, EXPIRES } = HEADERS;

export function arrangeUploadedFiles(files) {
  function reduceFiles(result, file, index) {
    return result.set(index, file);
  }

  return files.reduce(reduceFiles, EMPTY.LIST).filter(isTruthy);
}

export function fileToArrayBuffer(file) {
  return new Promise(function read(resolve, reject) {
    const reader = new FileReader();

    reader.addEventListener('error', function onError(event) {
      reject(event.target.error);
    });

    reader.addEventListener('loadend', function onloadEnd(event) {
      resolve(event.target.result);
    });

    reader.readAsArrayBuffer(file);
  });
}

export function formatTimestamp(timestamp) {
  if (!timestamp) return '';

  try {
    const { days, hours, minutes, months, seconds, years } = getDateDifference(getActualDate(), new Date(timestamp));
    let message = 'Updated ';

    if (years || months || days) {
      message += `${getFormattedDate(timestamp)}`;
    } else if (hours) {
      message += `${pluralize('hour', hours, true)} ago`;
    } else if (minutes) {
      message += `${pluralize('minute', minutes, true)} ago`;
    } else if (seconds) {
      message += 'just now';
    } else {
      message += 'while ago';
    }

    return message;
  } catch (error) {
    return '';
  }
}

export function downloadFile({ data, name }) {
  const a = document.createElement('a');
  a.style = 'display: none';
  a.href = data;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function shapeFileForApp({ channel, file: source }) {
  if (!(source instanceof File)) {
    throw new Error('Invalid file');
  }

  const name = source.name.replace(/\s+/g, function replacer(substring, index, input) {
    return !index || /[\W_]/.test(`${input.charAt(index - 1)}${input.charAt(index + 1)}`)
      ? ''
      : SEPARATOR.UNDERSCORE;
  });

  const file = new File([source], name, source);
  const error = getFileError({ channel, file });

  if (error) {
    throw new Error(error);
  }

  return file;
}

export function uploadFile({ db, file, shopId, signal, userId }) {
  return new Promise(async function executor(resolve, reject) {
    if (!(file instanceof File)) {
      reject('Invalid file');
      return;
    }

    const { name, size: bytes, type } = file;
    const size = formatFileSize(bytes);

    function onUrl({ uploadUrl }) {
      if (signal.aborted) return;

      function onUpload() {
        if (signal.aborted) return;

        const url = `${ENDPOINT.AWS_S3_FILES}/${userId}/${db}/${shopId}/${name}`;

        resolve(Map({ file_url: url, name, size }));
      }

      if (!uploadUrl) {
        reject('Failed to get signed upload URL');
        return;
      }

      api
        .put({ headers: { [CONTENT_TYPE]: type, [EXPIRES]: 0 }, payload: file, signal, url: uploadUrl })
        .then(onUpload)
        .catch(reject);
    }

    const params = { contentType: type, db, filename: name };

    api
      .get({ endpoint: ENDPOINT.WEB_V2, params, signal, url: `/files/${shopId}/uploadUrl` })
      .then(onUrl)
      .catch(reject);
  });
}
